/**
 * Correcting how the editor read an ingredient line (follow-up to #792).
 *
 * The hint under the line being typed shows the parser's reading as pills; a
 * cook who disagrees taps one and picks a quantity or a unit. The pick
 * **rewrites the line's text**, because the text is what the recipe screen,
 * scaling and the grocery list read — a separate override would be ignored by
 * all three. So the picker only offers units the parser reads (`pickerUnits`,
 * pinned by a round-trip test); « Autre… » still writes the cook's word into
 * the line, and is logged for the weekly review that teaches the parser.
 */

import { formatQuantity, parseIngredientText, parseNumStr } from './ingredientParser';

export type LineLanguage = 'fr' | 'en';

export interface LineParts {
  /** null when the parser found no quantity. */
  quantity: number | null;
  /** The unit as written ("Litres"), or '' when none was read. */
  unit: string;
  food: string;
}

/** A unit offered by the picker: its singular, and its plural when it has one. */
interface PickerUnit {
  one: string;
  many: string;
}

const UNITS: Record<LineLanguage, PickerUnit[]> = {
  fr: [
    { one: 'tasse', many: 'tasses' },
    { one: 'c. à s.', many: 'c. à s.' },
    { one: 'c. à t.', many: 'c. à t.' },
    { one: 'ml', many: 'ml' },
    { one: 'L', many: 'L' },
    { one: 'g', many: 'g' },
    { one: 'kg', many: 'kg' },
    { one: 'gousse', many: 'gousses' },
    { one: 'pincée', many: 'pincées' },
    { one: 'tranche', many: 'tranches' },
    { one: 'boîte', many: 'boîtes' },
  ],
  en: [
    { one: 'cup', many: 'cups' },
    { one: 'tbsp', many: 'tbsp' },
    { one: 'tsp', many: 'tsp' },
    { one: 'ml', many: 'ml' },
    { one: 'L', many: 'L' },
    { one: 'g', many: 'g' },
    { one: 'kg', many: 'kg' },
    { one: 'oz', many: 'oz' },
    { one: 'lb', many: 'lb' },
    { one: 'clove', many: 'cloves' },
    { one: 'pinch', many: 'pinches' },
    { one: 'can', many: 'cans' },
  ],
};

/** The units the picker offers, singular, in the order shown. */
export function pickerUnits(lang: LineLanguage): string[] {
  return UNITS[lang].map(u => u.one);
}

export function linePartsOf(line: string): LineParts {
  const text = line.trim();
  if (!text) return { quantity: null, unit: '', food: '' };
  const { numPart, unitPart, remainder } = parseIngredientText(text);
  const amount = numPart ? parseNumStr(numPart) : 0;
  return {
    quantity: amount > 0 ? amount : null,
    unit: amount > 0 ? unitPart : '',
    food: remainder.trim(),
  };
}

/** A known unit in the form its quantity wants; anything else as written. */
function inflect(unit: string, quantity: number): string {
  const lower = unit.toLowerCase();
  for (const known of [...UNITS.fr, ...UNITS.en]) {
    if (known.one.toLowerCase() === lower || known.many.toLowerCase() === lower) {
      return quantity > 1 ? known.many : known.one;
    }
  }
  return unit;
}

const STARTS_WITH_VOWEL = /^[aeiouyhàâäéèêëîïôöùûüœæ]/i;

/** « de sauce », « d'huile » — French puts a « de » between a unit and its food. */
function joinFood(unit: string, food: string, lang: LineLanguage): string {
  if (!unit || lang !== 'fr') return food;
  return STARTS_WITH_VOWEL.test(food) ? `d'${food}` : `de ${food}`;
}

/** The food without a typed unit stuck to its front (« filet d'huile » → « huile »). */
export function withoutLeadingUnit(food: string, unit: string): string {
  if (!unit) return food;
  const escaped = unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const lead = new RegExp(`^${escaped}(?:\\s+(?:de\\s+|du\\s+|des\\s+|d['’]|of\\s+)|\\s*d['’]|\\s+)`, 'i');
  return lead.test(food) ? food.replace(lead, '').trim() : food;
}

/**
 * The line rewritten with the cook's choice. `quantity: null` (or 0) drops the
 * quantity and the unit with it; `unit: ''` drops the unit only. A field left
 * out keeps what the line already had.
 */
export function rewriteIngredientLine(
  line: string,
  change: { quantity?: number | null; unit?: string },
  lang: LineLanguage,
): string {
  const parts = linePartsOf(line);
  const quantity = change.quantity !== undefined ? change.quantity : parts.quantity;
  const chosenUnit = change.unit !== undefined ? change.unit.trim() : parts.unit;
  const food = change.unit !== undefined ? withoutLeadingUnit(parts.food, chosenUnit) : parts.food;

  if (!quantity || quantity <= 0) return food;

  const unit = chosenUnit ? inflect(chosenUnit, quantity) : '';
  const amount = formatQuantity(quantity, unit);
  return [amount, unit, joinFood(unit, food, lang)].filter(Boolean).join(' ');
}

/**
 * What `ingredient_line_corrected` carries, or null when the line is unchanged.
 * The weekly review reads these to teach the parser what it missed.
 */
export function correctionEvent(
  typed: string,
  corrected: string,
  lang: LineLanguage,
  otherUnit?: string,
): Record<string, string | number | null> | null {
  if (typed.trim() === corrected.trim()) return null;
  const read = linePartsOf(typed);
  const now = linePartsOf(corrected);
  return {
    typed,
    read_quantity: read.quantity,
    read_unit: read.unit,
    corrected,
    corrected_quantity: now.quantity,
    corrected_unit: now.unit,
    other_unit: otherUnit?.trim() || null,
    language: lang,
  };
}
